import { m, type Message } from '@/i18n';
import type { Level, Verdict } from './constants';
import type { GateMode, FlowPhaseRow, MeansPair } from './stage-gates';
import { errOf, ok, type Result } from './types';

/**
 * Flows, the pure core (docs/FLOW.md): an item's membership in a flow is a
 * projection row (`flow_memberships`) holding a logged position (`flowPhaseKey`)
 * plus a per-member `overlay` of gate divergence. This file owns the pure
 * membership/advance decisions - join / leave / advance, the overlay folds,
 * the phase-compile rule - and never touches I/O; the server writers
 * (`src/server/flows.ts`) gather the facts and apply the effects.
 *
 * The gate convention (carried over from `decideAdvance`, stage-gates.ts): a
 * stage row's gate guards the EXIT of that stage - entering stage i checks
 * row i-1's gate. Moving forward from index f to index t therefore evaluates
 * every row in rows[f..t-1] (the crossed boundaries). Moving BACKWARD is free:
 * regression is a first-class, unflagged move.
 */

/** The flow's module-data namespace on member items (docs/FLOW.md: flow-field
 *  values land under the flow's own key, arriving by membership). Pure - shared
 *  by the write spine (advance predicate gather) and the read layer (strip). */
export function flowModuleKey(flowId: string): `flow:${string}` {
  return `flow:${flowId}`;
}

/** The inverse parser: the flow definition id carried by a `flow:<id>` module
 *  key, or null when the key is not a flow key. Mirrors `ownerRefOfModuleKey`
 *  for the `ctx:` namespace (domain/definitions.ts). */
export function flowIdOfModuleKey(key: string): string | null {
  return key.startsWith('flow:') && key.length > 'flow:'.length ? key.slice('flow:'.length) : null;
}

/**
 * Per-member gate divergence, keyed by stage key (docs/FLOW.md): the member's
 * deviations from the flow definition's gate at that stage.
 *   - `addConditions`: extra item refs this member's gate also waits on
 *     (Conditional-Go follow-ups, pre-staged work, spawned mandatory work).
 *   - `waived`: refs of the gate's conditions a gatekeeper waived for this
 *     member - a logged escape, visible forever, never a silent removal.
 */
export type FlowMemberOverlay = Record<
  string,
  {
    addConditions?: string[];
    waived?: string[];
  }
>;

/** The union-minus-waived fold (docs/FLOW.md): a member's EFFECTIVE gate
 *  conditions at a stage are the definition's list plus the member's added
 *  conditions, minus what a gatekeeper waived for this member. */
export function effectiveGateConditions(row: FlowPhaseRow, overlay: FlowMemberOverlay): string[] {
  const o = overlay[row.key];
  const union = [...new Set([...(row.gate.condition ?? []), ...(o?.addConditions ?? [])])];
  const waived = new Set(o?.waived ?? []);
  return union.filter((id) => !waived.has(id));
}

/** Immutable overlay update: append-dedupe `addConditions` / `waived` for one
 *  stage key. The only way overlay state grows (waives and conditions are never
 *  silently removed; a removal would be a new, designed act). */
export function overlayWith(
  overlay: FlowMemberOverlay,
  flowPhaseKey: string,
  patch: { addConditions?: string[]; waived?: string[] },
): FlowMemberOverlay {
  const cur = overlay[flowPhaseKey] ?? {};
  const next = { ...cur };
  if (patch.addConditions !== undefined && patch.addConditions.length > 0) {
    next.addConditions = [...new Set([...(cur.addConditions ?? []), ...patch.addConditions])];
  }
  if (patch.waived !== undefined && patch.waived.length > 0) {
    next.waived = [...new Set([...(cur.waived ?? []), ...patch.waived])];
  }
  return { ...overlay, [flowPhaseKey]: next };
}

/** Decide a join (docs/FLOW.md "Membership and position"): default position is
 *  the first stage; an explicit `flowPhaseKey` declares where the item honestly
 *  stands (retrofit / mid-flow adoption) - joining declares position, it never
 *  evaluates gates. A **fields-only flow** (a "type": no stages, docs/VIEWS.md
 *  §2a) has no position to take - a member joins at the **unstaged** position
 *  (`''`, the no-container lane) to acquire the flow's fields; an explicit stage
 *  is meaningless there and is refused. */
export function decideJoinFlow(
  rows: FlowPhaseRow[],
  existing: { flowPhaseKey: string; shared: boolean } | null,
  flowPhaseKey?: string,
  /** Would this join seat a SHARED membership (the flow has an audience AND
   *  the caller's share gate passed - both gathered by the server)? On an
   *  existing UNSHARED membership this makes the re-join the sanctioned
   *  share-flip gesture instead of a refusal (docs/FLOW.md "Access"). */
  wouldShare = false,
): Result<{ flowPhaseKey: string; flip: boolean }> {
  if (existing) {
    if (wouldShare && !existing.shared) {
      return ok({ flowPhaseKey: existing.flowPhaseKey, flip: true });
    }
    return errOf(
      'invalid_state',
      m('flowErrors.flow.alreadyMember', { stage: existing.flowPhaseKey }),
    );
  }
  if (rows.length === 0) {
    if (flowPhaseKey !== undefined && flowPhaseKey !== '') {
      return errOf('not_found', m('flowErrors.flow.noStageFieldsOnly', { stage: flowPhaseKey }));
    }
    return ok({ flowPhaseKey: '', flip: false });
  }
  if (flowPhaseKey === undefined) return ok({ flowPhaseKey: rows[0]!.key, flip: false });
  const row = rows.find((r) => r.key === flowPhaseKey);
  if (!row) return errOf('not_found', m('flowErrors.flow.noStage', { stage: flowPhaseKey }));
  return ok({ flowPhaseKey, flip: false });
}

/** Decide a leave: only a member can leave. Leaving is always legal (membership
 *  is opt-in association, never a wall); the position it abandons rides the
 *  event as history. */
export function decideLeaveFlow(existing: { flowPhaseKey: string } | null): Result<void> {
  if (!existing) return errOf('not_found', m('flowErrors.flow.notMember'));
  return ok(undefined);
}

/** The facts the server gathers for one advance decision (docs/FLOW.md "Gates
 *  as transition predicates"). Everything here is data; the decision is pure. */
export interface AdvanceMemberInput {
  rows: FlowPhaseRow[];
  /** the member's per-stage gate divergence (feeds the effective-conditions fold). */
  overlay: FlowMemberOverlay;
  fromFlowPhaseKey: string;
  toFlowPhaseKey: string;
  /** among every crossed row's effective condition refs: the ids whose verdict
   *  is `accepted`. */
  acceptedIds: ReadonlySet<string>;
  /** whether every `requiredFields` key of the TARGET row is filled on the
   *  member (under the flow's `flow:<flowId>` module namespace). Rows without
   *  requiredFields pass `true`. */
  requiredFieldsFilled: boolean;
  /** stage keys whose gate's gatekeeper rule the actor satisfies: person-ref
   *  equality, transitive membership of a gatekeeper group, or - when no
   *  gatekeeper is configured - a requester seat on the member (the old
   *  model's default: the gate falls to the item's owners). */
  actorIsGatekeeperOf: ReadonlySet<string>;
  /** owner reach on the member (requester / maintainer / admin): may act
   *  through an enforced gate, at `critical` (power-with-a-mark). */
  actsThrough: boolean;
}

export interface AdvanceMemberDecision {
  fromIndex: number;
  toIndex: number;
  /** the rows whose exit gates this move evaluates (empty for a backward move). */
  crossed: FlowPhaseRow[];
  /** undefined = clean pass (routine); `warning` = an open gate's predicates
   *  were unmet (flagged, Prinzip 20); `critical` = an enforced gate was passed
   *  via owner reach rather than a clean gatekeeper pass. */
  level: Level | undefined;
  /** human-legible predicate failures across the crossed gates - the standing
   *  facts behind a `warning` pass, and the record of what an act-through
   *  overrode. Empty on a clean pass or a backward move. */
  unmet: Message[];
}

/**
 * Decide an advance (docs/FLOW.md): moving a member from its current stage to
 * `toFlowPhaseKey`. Backward moves short-circuit - no predicates, no flag (the
 * regression move is free by design). Forward moves evaluate EVERY crossed
 * row's gate:
 *   - effective conditions (union minus waived) must all be `accepted`;
 *   - the TARGET row's `requiredFields` must be filled (entry predicate,
 *     evaluated at the final crossed boundary);
 *   - entering the LAST row additionally tests the member's own verdict
 *     (`accepted`) - the verdict axis stays a two-party act, the terminal
 *     stage only TESTS it (never sets it);
 *   - an `enforced` gate needs the gatekeeper as actor for a clean pass;
 *     owner reach acts through at `critical`; anyone else is `forbidden`
 *     (with the gate named). An `open` gate never walls: unmet predicates
 *     flag the move `warning` and ride `unmet`.
 */
export function decideAdvanceMember(input: AdvanceMemberInput): Result<AdvanceMemberDecision> {
  const { rows } = input;
  const fromIndex = rows.findIndex((r) => r.key === input.fromFlowPhaseKey);
  if (fromIndex === -1) {
    return errOf(
      'not_found',
      m('flowErrors.flow.currentStageGone', { stage: input.fromFlowPhaseKey }),
    );
  }
  const toIndex = rows.findIndex((r) => r.key === input.toFlowPhaseKey);
  if (toIndex === -1)
    return errOf('not_found', m('flowErrors.flow.noStage', { stage: input.toFlowPhaseKey }));
  if (toIndex === fromIndex) return errOf('no_change', m('flowErrors.flow.alreadyAtStage'));

  const target = rows[toIndex]!;

  // Backward move: free, unflagged, no predicates (regression is first-class).
  // (The target stage's `means` APPLY on any move - forward or regression -
  // but the apply plan is the server's to compile: docs/FLOW_DEFINITION.md §11,
  // `meansApplyPlan` in flow-definition.ts.)
  if (toIndex < fromIndex) {
    return ok({ fromIndex, toIndex, crossed: [], level: undefined, unmet: [] });
  }

  const crossed = rows.slice(fromIndex, toIndex);
  const unmet: Message[] = [];
  let warning = false;
  let critical = false;

  for (const [i, row] of crossed.entries()) {
    const failures: Message[] = [];
    for (const id of effectiveGateConditions(row, input.overlay)) {
      if (!input.acceptedIds.has(id)) {
        failures.push(m('flows.unmet.condition', { stage: row.label, condition: id }));
      }
    }
    // The final crossed boundary is the entry into the target: the target's
    // required fields are the entry predicate evaluated here, under this gate's
    // mode. (Whether a stage requires a verdict is FLOW-AUTHORED now, not a
    // hardcoded terminal-stage coupling - docs/archive/AUTOMATION_AUDIT.md, ruling 4:
    // the optional review path. A required verdict rides an authored gate
    // condition, absent by default.)
    if (i === crossed.length - 1) {
      const required = target.gate.requiredFields ?? [];
      if (required.length > 0 && !input.requiredFieldsFilled) {
        failures.push(m('flows.unmet.requiredFields', { stage: target.label, fields: required }));
      }
    }
    const predicatesMet = failures.length === 0;
    unmet.push(...failures);

    if (row.gate.mode === 'enforced') {
      const cleanPass = predicatesMet && input.actorIsGatekeeperOf.has(row.key);
      if (cleanPass) continue;
      if (!input.actsThrough) {
        return errOf(
          'forbidden',
          predicatesMet
            ? m('flowErrors.gate.enforcedNeedsGatekeeper', { stage: row.label })
            : m('flowErrors.gate.enforcedUnmet', { stage: row.label }),
        );
      }
      critical = true; // owner reach acts through: power-with-a-mark
    } else if (!predicatesMet) {
      warning = true; // open gate: surface over forbid
    }
  }

  const level: Level | undefined = critical ? 'critical' : warning ? 'warning' : undefined;
  return ok({ fromIndex, toIndex, crossed, level, unmet });
}

// --- derived member facts (pure; the read layer gathers, this decides) ---------
// The strip's stage steps, REUSING the advance predicate semantics above
// rather than inventing a parallel one. A stage AHEAD of the member is judged by
// the boundary INTO it - exactly what `decideAdvanceMember` evaluates for the
// single step across that boundary: the PREVIOUS row's effective exit conditions,
// THIS row's requiredFields (entry predicate), and the member's own verdict when
// this row is terminal. Position is declared truth: stages behind the member are
// `passed` because the member stands past them, never because predicates hold;
// a future stage is never `passed`, however green its boundary looks.

export type MemberFlowPhaseState = 'passed' | 'current' | 'blocked' | 'open';

/** One effective gate condition of a stage, as the member sees it (union of the
 *  definition's list and the overlay's additions; waived ones stay listed - a
 *  waive is a visible escape, never a silent removal). */
export interface MemberGateCondition {
  id: string;
  /** null = the ref was not gathered (dangling/cross-org) - reads as not accepted. */
  verdict: Verdict | null;
  /** The condition item's delivery claim, or null when it is not visible. */
  delivered: boolean | null;
  accepted: boolean;
  waived: boolean;
}

export interface MemberFlowPhaseFact {
  key: string;
  label: string;
  /** The stage's `means` pairs (docs/FLOW_DEFINITION.md §11): what moving
   *  here applies, rendered by the strip in the one pair vocabulary. */
  means: MeansPair[];
  gate: { mode: GateMode; gatekeeper: string | null };
  /** The stage step: `passed` = BEHIND the member's position (declared truth, not a
   *  predicate result); `current` = the position; a stage AHEAD is judged by
   *  the boundary into it - unmet predicates under an `enforced` gate read
   *  `blocked`, anything else reads `open` (an open gate moves flagged; empty
   *  `unmet` means the single step in would go clean). Never `passed` ahead. */
  state: MemberFlowPhaseState;
  /** Legible unmet predicates on the boundary INTO this stage (ahead stages
   *  only), in the `decideAdvanceMember` vocabulary: the previous row's exit
   *  conditions and this row's requiredFields (a required verdict is an
   *  authored gate condition now, not synthesized). Empty for `passed` and
   *  `current` stages. */
  unmet: Message[];
  /** The stage's effective conditions (waived ones included, marked). */
  conditions: MemberGateCondition[];
}

export interface MemberFactsInput {
  rows: FlowPhaseRow[];
  overlay: FlowMemberOverlay;
  /** the member's logged position. */
  flowPhaseKey: string;
  /** delivery/verdict snapshot per condition ref (gathered by the read layer);
   *  a missing id reads as not accepted. */
  conditionItems: ReadonlyMap<string, { delivered: boolean; verdict: Verdict }>;
  /** per stage key: whether every `requiredFields` key of THAT row is filled on
   *  the member (under the flow's namespace). Missing key reads as unfilled. */
  requiredFilled: ReadonlyMap<string, boolean>;
}

export interface MemberFacts {
  /** the member's position among the rows; -1 = the position is no longer in
   *  the flow (a drift fact, not an error - the definition moved under them). */
  flowPhaseIndex: number;
  stages: MemberFlowPhaseFact[];
}

/**
 * Derive the strip facts for one flow member (docs/FLOW.md). Pure: the read
 * layer gathers the condition snapshots and required-field fill states; this
 * folds them through the same predicate semantics `decideAdvanceMember` applies,
 * so the strip and the advance can never disagree about what a gate waits on.
 */
export function deriveMemberFacts(input: MemberFactsInput): MemberFacts {
  const { rows } = input;
  const flowPhaseIndex = rows.findIndex((r) => r.key === input.flowPhaseKey);

  const stages: MemberFlowPhaseFact[] = rows.map((row, i) => {
    const effective = new Set(effectiveGateConditions(row, input.overlay));
    const o = input.overlay[row.key];
    const union = [...new Set([...(row.gate.condition ?? []), ...(o?.addConditions ?? [])])];
    const conditions: MemberGateCondition[] = union.map((id) => {
      const snap = input.conditionItems.get(id);
      return {
        id,
        verdict: snap?.verdict ?? null,
        delivered: snap ? snap.delivered : null,
        accepted: snap?.verdict === 'accepted',
        waived: !effective.has(id),
      };
    });

    // The stage steps. Behind the position is `passed` (declared, not derived) and
    // the position itself `current` - neither re-litigates predicates. A stage
    // AHEAD is judged by the boundary INTO it, exactly as the single-step
    // advance across it would be (decideAdvanceMember): the PREVIOUS row's
    // effective exit conditions and THIS row's requiredFields (entry
    // predicate). A required verdict is an authored gate condition now.
    let state: MemberFlowPhaseState;
    const unmet: Message[] = [];
    if (i === flowPhaseIndex) {
      state = 'current';
    } else if (i < flowPhaseIndex) {
      state = 'passed';
    } else {
      const prev = rows[i - 1];
      if (prev) {
        for (const id of effectiveGateConditions(prev, input.overlay)) {
          if (input.conditionItems.get(id)?.verdict !== 'accepted') {
            unmet.push(m('flows.unmet.condition', { stage: prev.label, condition: id }));
          }
        }
      }
      const required = row.gate.requiredFields ?? [];
      if (required.length > 0 && input.requiredFilled.get(row.key) !== true) {
        unmet.push(m('flows.unmet.requiredFields', { stage: row.label, fields: required }));
      }
      // A required verdict is FLOW-AUTHORED now (docs/archive/AUTOMATION_AUDIT.md,
      // ruling 4), not a hardcoded terminal-stage coupling - so no verdict
      // predicate is synthesized here; it rides an authored gate condition.
      // A future stage is never `passed`: unmet under an enforced boundary
      // walls (`blocked`); everything else is reachable (`open` - an empty
      // `unmet` means the step in would go clean, a non-empty one flags it).
      state = unmet.length > 0 && prev?.gate.mode === 'enforced' ? 'blocked' : 'open';
    }

    return {
      key: row.key,
      label: row.label,
      means: row.means,
      gate: { mode: row.gate.mode, gatekeeper: row.gate.gatekeeper ?? null },
      state,
      unmet,
      conditions,
    };
  });

  return { flowPhaseIndex, stages };
}

/** Unmet lines side by side ("... · ..."); `undefined` for none. */
export function joinUnmet(lines: readonly Message[]): Message | undefined {
  if (lines.length === 0) return undefined;
  return lines.reduceRight((second, first) => m('flows.unmet.joined', { first, second }));
}
